import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Maximize2,
  MoreHorizontal,
  MessageSquare,
  ChevronDown,
  ArrowRight,
  CornerDownLeft,
  Paperclip,
  Globe,
  Send,
  Mic,
  ExternalLink,
  Check,
  Loader2,
  Clock,
  Trash2,
} from "lucide-react";
import chatService, {
  THINKING_PHRASES,
  THINKING_STATUSES,
  INTRO_QUESTIONS,
} from "../../services/chatService";
import { useUser } from "../../contexts/UserContext";
import supportService from "../../services/supportService";
import NeuralSentry from "./NeuralSentry";
import MiniSentry from "./MiniSentry";
import { trackTrigger } from "../../utils/truConversion";

/**
 * CONFIG & CONSTANTS
 */
const ASSISTANT_NAME = "Neuralward AI";

/**
 * CUSTOM ICONS
 */
const QuoteArrow = ({ size = 16, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M7 7v6a4 4 0 0 0 4 4h7" />
    <path d="M15 14l3 3-3 3" />
  </svg>
);


const DashedBox = () => (
  <div className="w-5 h-5 rounded-sm border-2 border-current border-dashed opacity-70" />
);

const NeuralwardLogo = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 156.2 186.8"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#ee2a7b"
      d="M105,34.34L87.17.26l39.56,11.54c10.12,3.58,19.86,7.37,29.47,12.05l-.07,72.15c0,7.12-1.51,13.6-3.75,20.17-5.67,16.62-17.74,31.38-31.08,42.16-.51-.17-.77-.54-1.12-1.15l-18.15-31.56-37.79-66.8-8.88-15.51-5.13-8.97s-12.82,4.04-22.23,7l.23,55.41c.03,6.6,2.9,12.91,6.04,18.45,5.16,9.09,12.6,15.57,21.02,21.58l.06-46.34,13.61,23.31,33.98,58.04-24.48,14.99-17.65-10.69-7.52-4.77c-19.14-12.38-39.71-30.35-47.84-52.29C2.46,110.97.21,102.87.19,94.12l-.19-69.9,10.1-4.62c10.35-4.73,20.98-7.92,31.88-11.27L69.12,0l5.79,10.61,15.61,28.1,1.23,2.2,29.75,53.76,7.12,13.29.05-66.22"
    />
  </svg>
);

const LinkedinLogo = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

/**
 * SUB-COMPONENTS
 */
const escapeHtml = (str) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const ContentRenderer = ({ content, onHighlight, onNavigate }) => {
  const formatText = (text) => {
    let lines = escapeHtml(text).split("\n");
    let formattedLines = lines.map((line) => {
      let trimmed = line.trim();
      if (trimmed.startsWith("* ") || trimmed.startsWith("- ")) {
        return `<li class="ml-6 mb-2 list-disc pl-2 marker:text-blue-400/60 font-normal">${trimmed.slice(2)}</li>`;
      }
      if (/^\d+\.\s/.test(trimmed)) {
        return `<li class="ml-6 mb-2 list-decimal pl-2 marker:text-blue-400/60 font-normal">${trimmed.replace(/^\d+\.\s/, "")}</li>`;
      }
      if (trimmed === "") return "<div class='h-4'></div>";
      return `<p class="mb-4">${line}</p>`;
    });

    let html = formattedLines.join("");
    html = html.replace(
      /\*\*(.*?)\*\*/g,
      '<strong class="text-white font-bold">$1</strong>',
    );

    // Support for standard markdown links [text](url) — internal paths and http(s) only
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, label, url) =>
      /^(\/|https?:\/\/)/i.test(url)
        ? `<a href="${url}" class="chat-product-link font-bold text-blue-300 border-b border-blue-500/30 hover:bg-blue-500/10 transition-all rounded px-1" data-internal="true">${label}</a>`
        : label,
    );

    html = html.replace(/\[\[(.*?)\]\]/g, (match, p1) => {
      return `<span class="highlight-trigger group font-bold text-blue-300 border-b border-blue-500/50 cursor-pointer hover:bg-blue-500/20 transition-all rounded px-1 relative inline-block" data-phrase="${p1}">${p1}
        <div class="absolute -top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all pointer-events-none flex flex-col items-center">
          <div class="bg-blue-500 text-white pl-4 pr-5 py-2.5 rounded-full text-[13px] font-bold shadow-xl flex items-center gap-2.5 whitespace-nowrap uppercase tracking-wider">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H16.017C15.4647 8 15.017 8.44772 15.017 9V12C15.017 12.5523 14.5693 13 14.017 13H13.017C12.4647 13 12.017 12.5523 12.017 12V9C12.017 6.79086 13.8079 5 16.017 5H19.017C21.2261 5 23.017 6.79086 23.017 9V15C23.017 18.3137 20.3307 21 17.017 21H14.017ZM2.017 21L2.017 18C2.017 16.8954 2.91243 16 4.017 16H7.017C7.56928 16 8.017 15.5523 8.017 15V9C8.017 8.44772 7.56928 8 7.017 8H4.017C3.46472 8 3.017 8.44772 3.017 9V12C3.017 12.5523 2.56928 13 2.017 13H1.017C0.46472 13 0.017 12.5523 0.017 12V9C0.017 6.79086 1.80786 5 4.017 5H7.017C9.22614 5 11.017 6.79086 11.017 9V15C11.017 18.3137 8.33071 21 5.017 21H2.017Z" /></svg>
            REPLY
          </div>
          <div class="w-3 h-3 bg-blue-500 rotate-45 -mt-1.5" />
        </div>
      </span>`;
    });
    return { __html: html };
  };

  return (
    <div
      className="text-[17px] leading-[1.85] text-slate-400/90 font-normal pr-4 animate-in fade-in duration-700 text-left"
      onClick={(e) => {
        const trigger = e.target.closest(".highlight-trigger");
        if (trigger) {
          e.stopPropagation();
          onHighlight(trigger.dataset.phrase);
          return;
        }

        const link = e.target.closest(".chat-product-link");
        if (link && link.dataset.internal === "true") {
          e.preventDefault();
          e.stopPropagation();
          const href = link.getAttribute("href");
          if (onNavigate) onNavigate(href);
        }
      }}
      dangerouslySetInnerHTML={formatText(content)}
    />
  );
};

const LeadForm = ({ onComplete, userMsg }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    comments: "",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle, loading, success, error, cooldown
  const [errors, setErrors] = useState({});
  const [, setLastSubmissionTime] = useState(null);
  const dropdownRef = useRef(null);

  // Mounting IS the form being served — this is the only place that fact exists.
  useEffect(() => {
    chatService.reportLeadFormShown(userMsg);
  }, [userMsg]);

  useEffect(() => {
    const lastSub = localStorage.getItem("lead_submission_timestamp");
    if (lastSub) {
      const elapsed = Date.now() - parseInt(lastSub);
      if (elapsed < 3600000) {
        setStatus("cooldown");
        setLastSubmissionTime(parseInt(lastSub));
      }
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name || formData.name.trim().length < 2)
      newErrors.name = true;
    if (!formData.email || !emailRegex.test(formData.email))
      newErrors.email = true;
    if (!formData.company || formData.company.trim().length < 2)
      newErrors.company = true;
    if (!formData.role) newErrors.role = true;
    if (!formData.comments || formData.comments.trim().length < 10)
      newErrors.comments = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      field === "email" &&
      formData.email &&
      !emailRegex.test(formData.email)
    ) {
      setErrors((prev) => ({ ...prev, email: true }));
    }
    if (field === "name" && formData.name && formData.name.trim().length < 2) {
      setErrors((prev) => ({ ...prev, name: true }));
    }
    if (
      field === "company" &&
      formData.company &&
      formData.company.trim().length < 2
    ) {
      setErrors((prev) => ({ ...prev, company: true }));
    }
    if (
      field === "comments" &&
      formData.comments &&
      formData.comments.trim().length < 10
    ) {
      setErrors((prev) => ({ ...prev, comments: true }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Security: Basic sanitization and hardening
    const sanitize = (str) => {
      if (!str) return "";
      return str.replace(/<[^>]*>?/gm, "").trim();
    };

    const sanitizedData = {
      name: sanitize(formData.name).substring(0, 100),
      email: sanitize(formData.email).substring(0, 100),
      company: sanitize(formData.company).substring(0, 100),
      role: formData.role, // role is from a fixed list
      comments: sanitize(formData.comments).substring(0, 1000),
    };

    setStatus("loading");
    try {
      const leadWebhook = process.env.REACT_APP_N8N_LEAD_WEBHOOK;
      if (leadWebhook) {
        await fetch(leadWebhook, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...sanitizedData, source: "chat_assistant" }),
        });
      }

      localStorage.setItem("lead_submission_timestamp", Date.now().toString());
      setStatus("success");
      trackTrigger("nw-sentry-lead-submitted");
      if (onComplete) onComplete();
    } catch (error) {
      console.error("Error saving lead:", error);
      setStatus("error");
    }
  };

  const roles = [
    { value: "ceo", label: "Chief Executive / Founder" },
    { value: "cto", label: "Chief Technology Officer" },
    { value: "finance", label: "Finance Manager / CFO" },
    { value: "product", label: "Product Director" },
    { value: "decision-maker", label: "Decision Maker" },
    { value: "developer", label: "Lead Developer / Architect" },
    { value: "operations", label: "Operations Manager" },
    { value: "security", label: "Cybersecurity Analyst" },
    { value: "other", label: "Other" },
  ];

  if (status === "success" || status === "cooldown") {
    const isCooldown = status === "cooldown";
    return (
      <div className="my-4 p-6 bg-[#ea3d95]/10 border border-[#ea3d95]/20 rounded-2xl flex items-center gap-4 animate-in zoom-in duration-500 w-full max-w-sm">
        <div className="h-10 w-10 shrink-0 rounded-full bg-[#ea3d95] flex items-center justify-center text-white">
          {isCooldown ? <Clock size={20} /> : <Check size={20} />}
        </div>
        <div>
          <h4 className="text-sm font-bold text-white">
            {isCooldown ? "Recent Request" : "Request Received"}
          </h4>
          <p className="text-xs text-slate-400">
            {isCooldown
              ? "We've received your recent briefing request and are already processing it. Please wait a bit before sending another."
              : "Our ecosystem orchestrators will contact you shortly."}
          </p>
        </div>
      </div>
    );
  }

  const inputClasses = (hasError) =>
    `w-full bg-white/[0.03] border rounded-2xl px-5 h-[52px] text-sm outline-none focus:outline-none focus:ring-1 transition-[background-color,border-color,box-shadow] duration-200 text-white placeholder:text-white/30 ${
      hasError
        ? "border-red-500/50 focus:ring-red-500/40 focus:border-red-500/40 bg-red-500/5"
        : "border-white/10 focus:ring-brandPrimary/40 focus:border-brandPrimary/30 focus:bg-white/[0.06]"
    }`;

  return (
    <div className="w-full my-[20px] space-y-8">
      {/* Header (No HR here anymore) */}
      <div
        id={`form-content-${onComplete ? "active" : "static"}`}
        className="space-y-6"
      >
        <div className="flex items-center gap-3">
          <div className="relative p-2 rounded-xl bg-brandPrimary/10 border border-brandPrimary/20">
            <NeuralwardLogo className="w-4 h-4 text-white" />
            <div className="absolute inset-0 bg-brandPrimary/20 blur-lg rounded-full -z-10 opacity-50" />
          </div>
          <h4 className="text-sm font-semibold text-white tracking-wide uppercase">
            Contact Us
          </h4>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="w-full space-y-5">
        {/* Identity Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-[10px] text-white/40 ml-1 uppercase tracking-wider font-medium">
              Full Name <span className="text-brandPrimary">*</span>
            </label>
            <input
              required
              type="text"
              placeholder="John Doe"
              maxLength={100}
              value={formData.name}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s.-]/g, "");
                setFormData({ ...formData, name: value });
                if (errors.name) setErrors({ ...errors, name: false });
              }}
              onBlur={() => handleBlur("name")}
              className={inputClasses(errors.name)}
              style={{ outline: "none", boxShadow: "none" }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] text-white/40 ml-1 uppercase tracking-wider font-medium">
              Work Email <span className="text-brandPrimary">*</span>
            </label>
            <input
              required
              type="email"
              placeholder="name@company.com"
              maxLength={100}
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: false });
              }}
              onBlur={() => handleBlur("email")}
              className={inputClasses(errors.email)}
              style={{ outline: "none", boxShadow: "none" }}
            />
          </div>
        </div>

        {/* Professional Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-[10px] text-white/40 ml-1 uppercase tracking-wider font-medium">
              Company <span className="text-brandPrimary">*</span>
            </label>
            <input
              required
              type="text"
              placeholder="Global Dynamics"
              maxLength={100}
              value={formData.company}
              onChange={(e) => {
                setFormData({ ...formData, company: e.target.value });
                if (errors.company) setErrors({ ...errors, company: false });
              }}
              onBlur={() => handleBlur("company")}
              className={inputClasses(errors.company)}
              style={{ outline: "none", boxShadow: "none" }}
            />
          </div>

          <div className="space-y-1.5 relative" ref={dropdownRef}>
            <label className="text-[10px] text-white/40 ml-1 uppercase tracking-wider font-medium">
              Role <span className="text-brandPrimary">*</span>
            </label>
            <div className="relative group/select">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`w-full bg-white/[0.03] border rounded-2xl px-5 h-[52px] text-sm outline-none focus:outline-none focus:ring-1 transition-[background-color,border-color,box-shadow] duration-200 text-white text-left flex items-center justify-between hover:bg-white/[0.06] ${
                  errors.role
                    ? "border-red-500/50 focus:ring-red-500/40 bg-red-500/5"
                    : "border-white/10 focus:ring-brandPrimary/40 focus:border-brandPrimary/30 hover:border-brandPrimary/40"
                }`}
                style={{ outline: "none", boxShadow: "none" }}
              >
                <span
                  className={formData.role ? "text-white" : "text-white/40"}
                >
                  {roles.find((r) => r.value === formData.role)?.label ||
                    "Select Role"}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-white/40 transition-all duration-300 ${isDropdownOpen ? "rotate-180 text-brandPrimary" : "group-hover/select:text-brandPrimary"}`}
                />
              </button>

              {/* Custom Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-[calc(100%+8px)] left-0 right-0 bg-[#121214]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl z-[150] overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                  <div className="py-2 max-h-[280px] overflow-y-auto custom-scrollbar">
                    {roles.map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, role: role.value });
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-5 py-4 text-[13px] transition-all hover:bg-white/5 flex items-center justify-between group/item ${formData.role === role.value ? "text-brandPrimary bg-brandPrimary/5" : "text-slate-300 hover:text-white"}`}
                      >
                        {role.label}
                        {formData.role === role.value && (
                          <Check size={14} className="text-brandPrimary" />
                        )}
                        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-brandPrimary scale-y-0 group-hover/item:scale-y-100 transition-transform origin-center" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message Area */}
        <div className="space-y-1.5 relative">
          <label className="text-[10px] text-white/40 ml-1 uppercase tracking-wider font-medium">
            Inquiry Details <span className="text-brandPrimary">*</span>
          </label>
          <div className="relative group/textarea">
            <textarea
              required
              placeholder="What can we solve for you?"
              value={formData.comments}
              maxLength={1000}
              onChange={(e) => {
                setFormData({ ...formData, comments: e.target.value });
                if (errors.comments) setErrors({ ...errors, comments: false });
              }}
              onBlur={() => handleBlur("comments")}
              rows={4}
              className={`w-full bg-white/[0.03] border rounded-2xl px-5 py-3 text-sm outline-none focus:outline-none focus:ring-1 transition-[background-color,border-color,box-shadow] duration-200 text-white placeholder:text-white/30 resize-none ${
                errors.comments
                  ? "border-red-500/50 focus:ring-red-500/40 bg-red-500/5"
                  : "border-white/10 focus:ring-brandPrimary/40 focus:border-brandPrimary/30 focus:bg-white/[0.06]"
              }`}
              style={{ outline: "none", boxShadow: "none" }}
            />
            <div className="absolute bottom-3 right-4 pointer-events-none">
              <span
                className={`text-[9px] font-bold tracking-widest transition-colors duration-300 ${formData.comments.length >= 900 ? "text-brandPrimary" : "text-white/10 group-focus-within/textarea:text-white/30"}`}
              >
                {formData.comments.length}/1000
              </span>
            </div>
          </div>
        </div>

        {/* Submission Button */}
        <div className="pt-2">
          <button
            disabled={status === "loading"}
            type="submit"
            className="group relative w-full h-[62px] overflow-hidden rounded-2xl bg-[#0a0a0c] border border-white/10 transition-all duration-500 hover:border-brandPrimary/50 hover:shadow-brand-glow active:scale-[0.99] disabled:opacity-50"
          >
            {/* Animated Backgrounds */}
            <div className="absolute inset-0 bg-brand-gradient opacity-80 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute inset-0 translate-x-[-100%] group-hover:animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            {/* Content Container */}
            <div className="relative z-10 flex items-center justify-center gap-3">
              {status === "loading" ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <>
                  <span className="text-white font-bold text-xs uppercase tracking-[0.35em] drop-shadow-lg transition-transform duration-500 group-hover:-translate-x-1">
                    Briefing Request
                  </span>
                  <div className="relative flex items-center justify-center">
                    <ArrowRight
                      size={16}
                      className="text-white transition-all duration-500 transform group-hover:translate-x-2 group-hover:opacity-100 opacity-70"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Inner Border Highlight */}
            <div className="absolute inset-0 border border-white/10 rounded-2xl pointer-events-none" />
          </button>

          {status === "error" && (
            <p className="text-[10px] text-red-400 text-center mt-4 font-medium tracking-tight uppercase">
              Interruption detected. Please try again.
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

const MessageItem = ({ message, onHighlight, onNavigate }) => {
  return (
    <div className="w-full pl-0 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex items-center gap-4 mb-4 select-none w-fit">
        <h3 className="text-[18px] font-bold text-white tracking-tight">
          {message.title || "Neural Orchestration"}
        </h3>
        <div className="flex items-center gap-1.5 rounded-lg bg-white/5 px-3 py-1 text-[12px] font-mono border border-white/5 text-slate-400">
          {message.timestamp || "—"}
        </div>
      </div>
      <ContentRenderer
        content={message.content}
        onHighlight={onHighlight}
        onNavigate={onNavigate}
      />
    </div>
  );
};

/**
 * SCRAMBLE TEXT — matrix-style character reveal, resolves left-to-right
 */
const SCRAMBLE_CHARS = 'abcdefghijklmnopqrstuvwxyz';

const ScrambleText = ({ text }) => {
  const [displayed, setDisplayed] = React.useState(text);
  const rafRef = React.useRef(null);
  const startRef = React.useRef(null);

  React.useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;
    const duration = Math.max(380, text.length * 20);

    const tick = (now) => {
      if (!startRef.current) startRef.current = now;
      const progress = Math.min((now - startRef.current) / duration, 1);
      const resolvedCount = Math.floor(progress * text.length);

      setDisplayed(
        text
          .split('')
          .map((ch, i) => {
            if (ch === ' ' || ch === '.') return ch;
            if (i < resolvedCount) return ch;
            return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
          })
          .join(''),
      );

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplayed(text);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [text]);

  return <>{displayed}</>;
};

/**
 * SESSION STORAGE UTILITIES
 */
const SESSIONS_KEY = 'nw_chat_sessions';
const TRANSFER_KEY = 'nw_chat_transfer';
const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const loadSessions = () => {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw).filter(s => Date.now() - s.savedAt < SESSION_TTL_MS);
  } catch { return []; }
};

const persistSessions = (sessions) => {
  try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions)); } catch {}
};

const nowDate = () =>
  new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatMsgDate = (dateStr) => {
  const today = nowDate();
  const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return dateStr || '';
};

/**
 * MAIN COMPONENT
 */
const NeuralAssistant = ({ standalone = false }) => {
  const navigate = useNavigate();
  const { user, userData } = useUser();
  // Support entitlement token, resolved once when the user profile loads. Sent
  // with the first chat message so Sentry knows what the account can get help on.
  const supportTokenRef = useRef("");
  useEffect(() => {
    let cancelled = false;
    if (!userData) { supportTokenRef.current = ""; return; }
    supportService.getEntitlement(userData)
      .then((e) => { if (!cancelled) supportTokenRef.current = supportService.entitlementToken(e); })
      .catch(() => { if (!cancelled) supportTokenRef.current = ""; });
    return () => { cancelled = true; };
  }, [userData]);
  const [introFinished, setIntroFinished] = useState(
    () => !!window.__nw_introDone || window.location.pathname !== "/",
  );
  const [personalize, setPersonalize] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isMinimized, setIsMinimized] = useState(() => standalone ? false : true);
  const [sessions, setSessions] = useState(() => loadSessions());
  const [userInput, setUserInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [thinkingPhase, setThinkingPhase] = useState("");
  const [thinkingStatus, setThinkingStatus] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [selectedChip, setSelectedChip] = useState(null);
  const [sentryInvoked, setSentryInvoked] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showNewChatMenu, setShowNewChatMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const [isVisible, setIsVisible] = useState(false);
  const [showChatIcon, setShowChatIcon] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const pulseRef = useRef(null);
  const shineRef = useRef(null);
  const entryTimerRef = useRef(null);
  const clickFocusingRef = useRef(false);
  const suggestionsRef = useRef(null);
  const chatIconTimerRef = useRef(null);
  const touchStartY = useRef(null);
  const currentSessionIdRef = useRef(`session_${Date.now()}`);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setIntroFinished(true);
    window.addEventListener("nw:introDone", handler);
    return () => window.removeEventListener("nw:introDone", handler);
  }, []);

  // Restart CSS animations when navigating between pages or changing visibility.
  // Uses a safer setTimeout delay to ensure the DOM is ready for the reflow.
  useEffect(() => {
    const timer = setTimeout(() => {
      const restart = (el, cls) => {
        if (!el) return;
        el.classList.remove(cls);
        void el.offsetWidth; // force reflow
        el.classList.add(cls);
      };
      restart(pulseRef.current, "animate-pulse-dot");
      restart(shineRef.current, "icon-shine");
    }, 500);
    return () => clearTimeout(timer);
  }, [location.key, isMinimized, isVisible]);

  const shouldHide = standalone ? false : (
    location.pathname.startsWith("/appcenter-store") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/auth/") ||
    location.pathname.startsWith("/gift/") ||
    location.pathname.startsWith("/profile") ||
    location.pathname === "/my-licenses" ||
    location.pathname === "/chat" ||
    !introFinished
  );

  // Deep link: another page (e.g. the My Licenses support link) queued a
  // question for Sentry — stored in sessionStorage because the assistant is
  // hidden on that page. Open with the question pre-filled (not auto-sent).
  useEffect(() => {
    if (shouldHide) return;
    const ask = sessionStorage.getItem("nw_sentry_ask");
    if (!ask) return;
    sessionStorage.removeItem("nw_sentry_ask");
    setUserInput(ask);
    const t = setTimeout(() => {
      setIsMinimized(false);
      setTimeout(() => {
        inputRef.current?.focus();
        setIsFocused(true);
      }, 720);
    }, 1200); // let the entry animation land first
    return () => clearTimeout(t);
  }, [shouldHide]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldHide) {
      setIsVisible(false);
      clearTimeout(entryTimerRef.current);
      return;
    }
    // On the homepage, stagger the chat icon 2s after the navbar slides in.
    // On other pages it appears after 1s (enough for the page to settle).
    const delay = location.pathname === "/" ? 1000 : 1000;
    entryTimerRef.current = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(entryTimerRef.current);
  }, [shouldHide]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMinimized) {
      setIsFocused(false);
      setSelectedIndex(-1);
    }
  }, [isMinimized]);

  useEffect(() => {
    setSuggestions(
      [...INTRO_QUESTIONS].sort(() => 0.5 - Math.random()).slice(0, 3),
    );
  }, []);

  useEffect(() => {
    if (!isProcessing) return;
    const pick = (current) => {
      const pool = THINKING_PHRASES.filter((p) => p !== current);
      return pool[Math.floor(Math.random() * pool.length)];
    };
    const first = pick('');
    setThinkingPhase(first);
    let timeout;
    const cycle = (current) => {
      timeout = setTimeout(() => {
        const next = pick(current);
        setThinkingPhase(next);
        cycle(next);
      }, 1600 + Math.random() * 2400);
    };
    cycle(first);
    return () => clearTimeout(timeout);
  }, [isProcessing]);

  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === "ai" && !isProcessing) {
        const messageElements =
          scrollRef.current.querySelectorAll(".message-wrapper");
        const lastElement = messageElements[messageElements.length - 1];
        if (lastElement) {
          lastElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
  }, [messages, isProcessing]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsFocused(false);
        setSelectedIndex(-1);
        setHoveredIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, drawerOpen]);

  useEffect(() => {
    if (isMinimized) return;
    const handleGlobalKeyDown = (e) => {
      if (e.target === inputRef.current) return;
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      )
        return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length === 1) inputRef.current?.focus();
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isMinimized]);

  // Cycle the chat icon back in briefly over the MiniSentry, starting once chat is active
  useEffect(() => {
    if (messages.length === 0) return;
    const schedule = () => {
      chatIconTimerRef.current = setTimeout(() => {
        setShowChatIcon(true);
        chatIconTimerRef.current = setTimeout(() => {
          setShowChatIcon(false);
          schedule();
        }, 2000 + Math.random() * 1500);
      }, 18000 + Math.random() * 20000);
    };
    schedule();
    return () => clearTimeout(chatIconTimerRef.current);
  }, [messages.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore session transferred from the main chat when opening in a new tab/popup
  useEffect(() => {
    if (!standalone) return;
    try {
      const raw = localStorage.getItem(TRANSFER_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (Date.now() - data.ts < 10000) {
        setMessages(data.messages || []);
        chatService.importHistory(data.chatHistory || []);
        localStorage.removeItem(TRANSFER_KEY);
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave current session whenever messages change (debounced, only when Keep Session is on)
  useEffect(() => {
    if (!personalize || !messages.length) return;
    const timer = setTimeout(() => {
      const sessionId = currentSessionIdRef.current;
      const title = messages.find(m => m.role === 'ai')?.title || 'Untitled Chat';
      const session = {
        id: sessionId,
        title,
        date: nowDate(),
        savedAt: Date.now(),
        messages: [...messages],
        chatHistory: chatService.exportHistory(),
      };
      setSessions(prev => {
        const idx = prev.findIndex(s => s.id === sessionId);
        const updated = idx >= 0
          ? prev.map((s, i) => i === idx ? session : s)
          : [session, ...prev].slice(0, 10);
        persistSessions(updated);
        return updated;
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [messages, personalize]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLatestLeadForm = (idx) => {
    const formIndices = messages
      .map((m, i) => (m.showLeadForm && !m.formSubmitted ? i : -1))
      .filter((i) => i !== -1);
    return (
      formIndices.length > 0 && formIndices[formIndices.length - 1] === idx
    );
  };

  const handleFormComplete = (idx) => {
    setMessages((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, formSubmitted: true } : m)),
    );
  };

  const scrollToLatestForm = () => {
    const formIndices = messages
      .map((m, i) => (m.showLeadForm && !m.formSubmitted ? i : -1))
      .filter((i) => i !== -1);
    const lastIdx = formIndices[formIndices.length - 1];
    if (lastIdx !== undefined) {
      const el = document.getElementById(`form-highlight-target-${lastIdx}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Solid border effect
        el.style.borderColor = "#ea3d95";
        setTimeout(() => {
          el.style.borderColor = "transparent";
        }, 2000);
      }
    }
  };
  const handleSendMessage = async (textOverride) => {
    const text = textOverride || userInput;
    if (!text.trim() || isProcessing) return;

    // Handle hidden Sentry commands
    const cmd = text.trim().toLowerCase();
    if (cmd.startsWith("/")) {
      const trigger = (mode) => {
        window.dispatchEvent(new CustomEvent("nw:sentryTrigger", { detail: mode }));
        setUserInput("");
        setIsFocused(false);
      };
      if (cmd === "/pong") { trigger("PONG"); return; }
      if (cmd === "/arkanoid") { trigger("ARKANOID"); return; }
      if (cmd === "/walle") { trigger("WALLE"); return; }
      if (cmd === "/logo") { trigger("LOGO"); return; }
      if (cmd === "/matrix") { trigger("MATRIX"); return; }
      if (cmd === "/2001") { trigger("MONOLITH"); return; }
      if (cmd === "/dna") { trigger("DNA_HELIX"); return; }
      if (cmd === "/ekg") { trigger("EKG"); return; }
      if (cmd === "/atom") { trigger("ATOM"); return; }
      if (cmd === "/tree") { trigger("BINARY_TREE"); return; }
      if (cmd === "/sentry") {
        setSentryInvoked(true);
        window.dispatchEvent(new CustomEvent("nw:sentryTrigger", { detail: "RESTING" }));
        setUserInput("");
        setIsFocused(false);
        return;
      }
    }

    const chipContext = selectedChip;
    setSentryInvoked(false);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text, chip: chipContext, timestamp: nowTime(), date: nowDate() },
    ]);
    setUserInput("");
    setSelectedChip(null);
    setIsFocused(false);
    setSelectedIndex(-1);
    setHoveredIndex(-1);

    setThinkingStatus(
      THINKING_STATUSES[Math.floor(Math.random() * THINKING_STATUSES.length)],
    );

    try {
      setIsProcessing(true);
      const personalizationData = user?.displayName
        ? { name: user.displayName, email: user.email || null, supportProducts: supportTokenRef.current }
        : null;
      const response = await chatService.sendMessage(text, chipContext, personalizationData);
      if (response.error) throw new Error(response.text);

      const hasPortfolioMention = response.text
        .toLowerCase()
        .includes("gonzalo de salterain");

      // chatService strips the marker and reports it as a flag — it is not always
      // placed above the SUGGESTIONS: line, so it cannot be found in response.text.
      const hasLeadAction = response.showLeadForm === true;
      const cleanedText = response.text;

      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: cleanedText,
          title: response.title,
          suggestions: response.suggestions,
          showPortfolio: hasPortfolioMention,
          showLeadForm: hasLeadAction,
          formSubmitted: false,
          timestamp: nowTime(),
          date: nowDate(),
        },
      ]);
    } catch (error) {
      const ERROR_CONTENT = [
        "I'm handling a lot right now. Give me a moment and try again.",
        "Too many threads running at once — send that again in a second?",
        "Something got tangled on my end. Ask again and I'll sort it out.",
        "I hit a snag on that one. Try your question again.",
        "My attention is stretched thin right now. Resend and I'll catch up.",
        "High traffic on my end — try again in a moment.",
        "I dropped the thread there. Ask me again and I'll pick it back up.",
        "I got turned around for a second. Send that again and I'll be with you.",
      ];
      const ERROR_TITLES = [
        "Sentry is Busy",
        "Sentry is Multitasking",
        "Sentry Overloaded",
        "Sentry Needs a Moment",
        "Sentry Hit a Snag",
        "Sentry Overclocked",
        "Sentry is Stretched",
        "Sentry Got Tangled",
      ];
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          content: ERROR_CONTENT[Math.floor(Math.random() * ERROR_CONTENT.length)],
          title: ERROR_TITLES[Math.floor(Math.random() * ERROR_TITLES.length)],
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!showNewChatMenu && !showMoreMenu) return;
    const close = () => { setShowNewChatMenu(false); setShowMoreMenu(false); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showNewChatMenu, showMoreMenu]);

  const clearChat = () => {
    currentSessionIdRef.current = `session_${Date.now()}`;
    setMessages([]);
    setSentryInvoked(false);
    chatService.clearHistory();
    setShowNewChatMenu(false);
    setShowMoreMenu(false);
  };

  const loadSession = (session) => {
    currentSessionIdRef.current = session.id;
    setMessages(session.messages || []);
    chatService.importHistory(session.chatHistory || []);
    setSentryInvoked(false);
    setShowNewChatMenu(false);
  };

  const deleteSession = (e, sessionId) => {
    e.stopPropagation();
    const updated = sessions.filter(s => s.id !== sessionId);
    persistSessions(updated);
    setSessions(updated);
  };

  const copyTranscript = () => {
    if (!messages.length) return;
    const text = messages
      .map((m) => {
        const who = m.role === 'user' ? 'You' : 'Sentry';
        const ts = m.date && m.timestamp ? `[${m.date} ${m.timestamp}] ` : '';
        return `${ts}${who}: ${m.content}`;
      })
      .join("\n\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 1800);
    });
    setShowNewChatMenu(false);
    setShowMoreMenu(false);
  };

  const handleKeyDown = (e) => {
    if (!isFocused) {
      if (e.key === "Enter") handleSendMessage();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev <= 0 ? suggestions.length - 1 : prev - 1,
      );
    } else if (e.key === "Escape") {
      setIsFocused(false);
      setSelectedIndex(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0) {
        handleSendMessage(suggestions[selectedIndex]);
      } else {
        handleSendMessage();
      }
    }
  };

  // True only when the expanded panel is actually visible to the user
  const panelShown = standalone || (isMobile ? drawerOpen : !isMinimized);

  const expandedContent = (
    <div
      className="flex flex-col h-full w-full"
      style={{
        opacity: !isMobile && isMinimized ? 0 : 1,
        transform: !isMobile && isMinimized ? "scale(0.85)" : "scale(1)",
        transition: "opacity 700ms, transform 700ms",
        pointerEvents: !isMobile && isMinimized ? "none" : "auto",
      }}
    >
      <header className="flex items-center justify-between px-4 py-4 md:px-8 md:py-6 border-b border-white/5 shrink-0 z-50 bg-transparent">
        <div className="flex items-center gap-3">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-slate-400 overflow-hidden">
            <span
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-700"
              style={{ opacity: !(messages.length > 0 || isProcessing) || showChatIcon ? 1 : 0 }}
            >
              <MessageSquare size={18} />
            </span>
            <span
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-700"
              style={{ opacity: (messages.length > 0 || isProcessing) && !showChatIcon ? 1 : 0 }}
            >
              {(messages.length > 0 || isProcessing) && <MiniSentry active />}
            </span>
          </div>
          {/* "New AI chat" dropdown */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowNewChatMenu((p) => !p); setShowMoreMenu(false); }}
              className="flex items-center gap-2 text-base font-semibold hover:bg-white/5 px-2 py-1 rounded-md transition-colors outline-none"
            >
              New AI chat{" "}
              <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${showNewChatMenu ? "rotate-180" : ""}`} />
            </button>
            {showNewChatMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-[#242424] border border-white/10 rounded-xl shadow-xl z-[200] overflow-hidden animate-in fade-in zoom-in-95 duration-150 origin-top-left">
                <button onClick={clearChat} className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5">
                  <MessageSquare size={14} className="text-slate-500" /> New chat
                </button>
                <button onClick={copyTranscript} disabled={!messages.length} className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                  {copyFeedback ? <Check size={14} className="text-green-400" /> : <ExternalLink size={14} className="text-slate-500" />}
                  {copyFeedback ? "Copied!" : "Copy transcript"}
                </button>
                {sessions.length > 0 && (
                  <>
                    <div className="h-px bg-white/5 mx-3 my-1" />
                    <div className="px-4 py-1.5">
                      <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Recent</span>
                    </div>
                    <div className="max-h-52 overflow-y-auto custom-scrollbar">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          onClick={() => loadSession(session)}
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center justify-between gap-2 cursor-pointer group/sess"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-medium text-slate-300 truncate">{session.title}</p>
                            <p className="text-[10px] text-slate-600">{session.date}</p>
                          </div>
                          <button
                            onClick={(e) => deleteSession(e, session.id)}
                            aria-label="Delete chat session"
                            className="opacity-0 group-hover/sess:opacity-100 p-1 hover:text-red-400 text-slate-600 transition-all shrink-0"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 md:gap-8">
          <div className="flex items-center gap-3 pr-5 border-r border-white/10 hidden md:flex">
            <span className="text-xs text-slate-400 font-medium">Keep Session</span>
            <button
              onClick={(e) => { e.stopPropagation(); setPersonalize(!personalize); }}
              aria-label="Toggle personalization"
              aria-pressed={personalize}
              className={`h-5 w-10 rounded-full transition-colors duration-200 p-1 flex items-center ${personalize ? "bg-blue-600" : "bg-zinc-700"}`}
            >
              <div className={`h-3 w-3 rounded-full bg-white transform transition-transform ${personalize ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          <div className="flex items-center gap-4 text-slate-400">
            {/* New chat */}
            <button onClick={clearChat} title="New chat" className="hover:text-white transition-colors p-1 hidden md:block">
              <MessageSquare size={20} />
            </button>
            {/* Maximize / restore */}
            {!standalone && (
            <button onClick={(e) => { e.stopPropagation(); setIsMaximized((p) => !p); }} title={isMaximized ? "Restore" : "Maximize"} className="hover:text-white transition-colors p-1 hidden md:block">
              <Maximize2 size={20} />
            </button>
            )}
            {/* Pop-out window */}
            {!standalone && (
            <button
              onClick={() => {
                localStorage.setItem('nw_chat_transfer', JSON.stringify({ messages, chatHistory: chatService.exportHistory(), ts: Date.now() }));
                window.open(`${window.location.origin}/chat`, '_blank', 'width=900,height=700,menubar=no,toolbar=no,location=no,status=no');
              }}
              title="Open in popup"
              className="hover:text-white transition-colors p-1 hidden md:block"
            >
              <DashedBox />
            </button>
            )}
            {/* Open in new tab */}
            {!standalone && (
            <button
              onClick={() => {
                localStorage.setItem('nw_chat_transfer', JSON.stringify({ messages, chatHistory: chatService.exportHistory(), ts: Date.now() }));
                window.open(`${window.location.origin}/chat`, '_blank');
              }}
              title="Open in new tab"
              className="hover:text-white transition-colors p-1 hidden md:block"
            >
              <ExternalLink size={20} />
            </button>
            )}
            {/* More options */}
            <div className="relative hidden md:block">
              <button onClick={(e) => { e.stopPropagation(); setShowMoreMenu((p) => !p); setShowNewChatMenu(false); }} title="More options" className="hover:text-white transition-colors p-1">
                <MoreHorizontal size={20} />
              </button>
              {showMoreMenu && (
                <div className="absolute top-full right-0 mt-1 w-44 bg-[#242424] border border-white/10 rounded-xl shadow-xl z-[200] overflow-hidden animate-in fade-in zoom-in-95 duration-150 origin-top-right">
                  <button onClick={copyTranscript} disabled={!messages.length} className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                    {copyFeedback ? <Check size={14} className="text-green-400" /> : <ExternalLink size={14} className="text-slate-500" />}
                    {copyFeedback ? "Copied!" : "Copy transcript"}
                  </button>
                  <div className="h-px bg-white/5 mx-3" />
                  <button onClick={clearChat} className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5 hover:text-red-300 transition-colors flex items-center gap-2.5">
                    <X size={14} /> Clear chat
                  </button>
                </div>
              )}
            </div>
            {!isMobile && !standalone && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
                aria-label="Minimize chat"
                className="ml-1 hover:text-white transition-all hover:rotate-90 p-1"
              >
                <X size={24} />
              </button>
            )}
          </div>
        </div>
      </header>

            <div className="flex-1 relative overflow-hidden flex flex-col">
              {/* panelShown gate: the panel stays mounted while minimized (opacity 0),
                  so without it the constellation renders at 60fps behind an invisible
                  panel on every page — it was the site's biggest main-thread cost. */}
              <NeuralSentry visible={panelShown && ((messages.length === 0 && !isProcessing) || sentryInvoked)} excludeRef={suggestionsRef} />
              <div
                className="flex-1 overflow-y-auto custom-scrollbar px-4 pt-5 pb-48 md:px-8 md:pt-8 md:pb-80"
                style={{ opacity: sentryInvoked ? 0 : 1, transition: 'opacity 800ms ease' }}
                ref={scrollRef}
                onClick={() => {
                  if (isFocused) {
                    setIsFocused(false);
                    setSelectedIndex(-1);
                  }
                }}
              >
                {(() => {
                  let lastDate = null;
                  return messages.map((msg, idx) => {
                    const showDateSep = msg.date && msg.date !== lastDate;
                    if (showDateSep) lastDate = msg.date;
                    return (
                      <React.Fragment key={idx}>
                        {showDateSep && (
                          <div className="flex items-center gap-3 my-4 select-none">
                            <div className="flex-1 h-px bg-white/5" />
                            <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-widest px-2">
                              {formatMsgDate(msg.date)}
                            </span>
                            <div className="flex-1 h-px bg-white/5" />
                          </div>
                        )}
                  <div
                    className={`message-wrapper mb-12 w-full flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
                  >
                    {msg.role === "user" ? (
                      <div className="flex flex-col items-end max-w-[80%]">
                        {msg.chip && (
                          <div className="flex mb-2">
                            <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1 scale-90 origin-right">
                              <QuoteArrow size={12} className="text-blue-400" />
                              <span className="text-[11px] font-semibold text-blue-200 italic">
                                {msg.chip}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="bg-white/5 border border-white/10 px-6 py-3 rounded-[24px] text-[17px] shadow-sm text-slate-200">
                          {msg.content}
                        </div>
                      </div>
                    ) : (
                      <>
                        <MessageItem
                          message={msg}
                          onHighlight={(p) => {
                            setSelectedChip(p);
                            inputRef.current?.focus();
                          }}
                          onNavigate={(path) => {
                            navigate(path);
                            setIsMinimized(true); // Minimize when navigating to a product
                          }}
                        />

                        {/* LEAD CAPTURE FORM (Moved above suggestions) */}
                        {msg.role === "ai" && msg.showLeadForm && (
                          <div className="w-full my-4">
                            <hr className="border-white/10 mb-6" />
                            <div className="relative w-full">
                              {/* Highlight Overlay - Expanded to not touch the form sides */}
                              <div
                                id={`form-highlight-target-${idx}`}
                                className="absolute -inset-x-6 -inset-y-4 border-2 border-transparent rounded-[32px] pointer-events-none transition-all duration-500 z-0"
                              />

                              <div className="relative z-10">
                                {isLatestLeadForm(idx) || msg.formSubmitted ? (
                                  <LeadForm
                                    onComplete={() => handleFormComplete(idx)}
                                    userMsg={messages[idx - 1]?.content || ""}
                                  />
                                ) : (
                                  <div className="my-4 p-5 bg-[#ea3d95]/5 border border-[#ea3d95]/10 rounded-2xl flex items-center justify-between group animate-in fade-in slide-in-from-left-2 duration-500">
                                    <div className="flex items-center gap-4">
                                      <div className="h-10 w-10 rounded-full bg-[#ea3d95]/10 flex items-center justify-center text-[#ea3d95] shadow-inner">
                                        <MessageSquare size={18} />
                                      </div>
                                      <div>
                                        <p className="text-sm font-bold text-white tracking-tight">
                                          Active Briefing Request
                                        </p>
                                        <p className="text-[11px] text-slate-500 font-medium">
                                          This form has been moved to the latest
                                          message
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={scrollToLatestForm}
                                      className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#ea3d95]/10 hover:bg-[#ea3d95]/20 text-[#ea3d95] text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 hover:scale-105 active:scale-95"
                                    >
                                      Contact Us
                                      <ArrowRight size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* SUGGESTIONS */}
                        {msg.suggestions && msg.suggestions.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2 animate-in fade-in slide-in-from-bottom-2 duration-700 delay-500">
                            {msg.suggestions.map((suggestion, sIdx) => (
                              <button
                                key={sIdx}
                                onClick={() => handleSendMessage(suggestion)}
                                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-[11px] text-slate-300 transition-all duration-300"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* INLINE PORTFOLIO BANNERS */}
                        {msg.role === "ai" && msg.showPortfolio && (
                          <div className="mt-6 flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-700">
                            <a
                              href="http://gonzalo.desalterain.com.ar"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group block flex-1 relative overflow-hidden rounded-xl bg-white/[0.03] backdrop-blur-md border border-white/10 p-4 hover:border-blue-500/30 transition-all duration-300"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                              <div className="relative flex items-center justify-between gap-8">
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded-full bg-[#ea3d95] flex items-center justify-center text-white shadow-lg">
                                    <Globe size={14} />
                                  </div>
                                  <div>
                                    <h4 className="text-[12px] font-bold text-white tracking-tight whitespace-nowrap">
                                      Gonzalo de Salterain
                                    </h4>
                                    <p className="text-[9px] font-medium text-slate-400 uppercase tracking-[0.15em] whitespace-nowrap">
                                      Official Portfolio
                                    </p>
                                  </div>
                                </div>
                                <ArrowRight
                                  size={14}
                                  className="text-blue-400 group-hover:translate-x-1 transition-transform"
                                />
                              </div>
                            </a>

                            <a
                              href="https://www.linkedin.com/in/gsaltera/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group block flex-1 relative overflow-hidden rounded-xl bg-white/[0.03] backdrop-blur-md border border-white/10 p-4 hover:border-blue-500/30 transition-all duration-300"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                              <div className="relative flex items-center justify-between gap-8">
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded-full bg-[#0A66C2] flex items-center justify-center text-white shadow-lg">
                                    <LinkedinLogo size={14} />
                                  </div>
                                  <div>
                                    <h4 className="text-[12px] font-bold text-white tracking-tight whitespace-nowrap">
                                      Gonzalo de Salterain
                                    </h4>
                                    <p className="text-[9px] font-medium text-slate-400 uppercase tracking-[0.15em] whitespace-nowrap">
                                      Official LinkedIn
                                    </p>
                                  </div>
                                </div>
                                <ArrowRight
                                  size={14}
                                  className="text-blue-400 group-hover:translate-x-1 transition-transform"
                                />
                              </div>
                            </a>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                      </React.Fragment>
                    );
                  });
                })()}

                {isProcessing && (
                  <div className="w-full relative pl-12 animate-in fade-in slide-in-from-left-4 duration-500">
                    <div className="relative mb-2 flex items-center h-10">
                      <div className="absolute -left-[48px] top-0 w-12 h-10 flex items-center justify-center bg-transparent">
                        <div className="h-6 w-6 border-[3px] border-slate-700 border-t-blue-500 rounded-full animate-spin" />
                      </div>
                      <span className="text-[17px] font-normal text-slate-400 tracking-tight italic flex items-center">
                        {ASSISTANT_NAME}&nbsp;&nbsp;
                        <span className="text-slate-300 font-medium not-italic">
                          <ScrambleText text={thinkingPhase} />
                        </span>
                      </span>
                    </div>
                    <div className="relative">
                      <div className="absolute left-[-24px] top-[34px] bottom-0 w-px bg-white/10 -translate-x-1/2" />
                      <div className="absolute -left-[24px] top-[9px] h-2.5 w-2.5 rounded-full bg-blue-500 border-[2px] border-[#1c1c1c] -translate-x-1/2 dot-pulse" />
                      <h3
                        key={thinkingStatus}
                        className="text-[17px] font-semibold text-white/50 tracking-wide animate-in fade-in duration-500"
                      >
                        {thinkingStatus}
                      </h3>
                    </div>
                  </div>
                )}
              </div>

              <div className={`absolute bottom-0 left-0 right-0 h-12 bg-[#1c1c1c] z-[90] pointer-events-none ${standalone ? '' : 'rounded-b-[40px]'}`} />
            </div>

            <div className="absolute bottom-4 left-3 right-3 md:bottom-6 md:left-6 md:right-6 z-[100]">
              <div className="w-full relative">
                <div
                  className={`absolute -top-32 left-0 right-0 h-32 bg-gradient-to-t from-[#1c1c1c] to-transparent pointer-events-none transition-opacity duration-500 ${isFocused ? "opacity-100" : "opacity-0"}`}
                />

                <div
                  className={`rounded-[32px] border shadow-[0_30px_60px_rgba(0,0,0,0.5)] overflow-hidden transition-all duration-300 ${isFocused ? "bg-white/[0.04] backdrop-blur-3xl border-white/5" : "bg-[#1a1a1a]/95 backdrop-blur-2xl border-white/5"}`}
                >
                  <div
                    ref={suggestionsRef}
                    style={{
                      transition: isFocused
                        ? "max-height 700ms cubic-bezier(0.34,1.56,0.64,1), opacity 700ms cubic-bezier(0.34,1.56,0.64,1), transform 700ms cubic-bezier(0.34,1.56,0.64,1)"
                        : "max-height 150ms ease-in, opacity 120ms ease-in, transform 120ms ease-in",
                    }}
                    className={`overflow-hidden ${
                      isFocused
                        ? "max-h-[350px] opacity-100 border-b border-white/5 translate-y-0"
                        : "max-h-0 opacity-0 translate-y-4 pointer-events-none"
                    }`}
                    onMouseLeave={() => setHoveredIndex(-1)}
                  >
                    {!isMobile && (
                    <div className="flex items-center justify-between px-8 pt-6 pb-2">
                      <div className="flex gap-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        <span className="flex items-center gap-2.5">
                          <span className="px-2 py-1 border border-white/10 rounded-md bg-white/5 shadow-inner text-slate-300">
                            ↑↓
                          </span>{" "}
                          TO NAVIGATE
                        </span>
                        <span className="flex items-center gap-2.5">
                          <span className="px-2 py-1 border border-white/10 rounded-md bg-white/5 shadow-inner text-slate-300">
                            <CornerDownLeft size={10} />
                          </span>{" "}
                          TO SELECT
                        </span>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2.5">
                        <span className="px-2 py-1 border border-white/10 rounded-md bg-white/5 shadow-inner text-slate-300 text-[9px]">
                          ESC
                        </span>{" "}
                        TO CLOSE
                      </span>
                    </div>
                    )}

                    <div className="px-4 pt-4 pb-2 space-y-1.5 md:px-8 md:pt-0">
                      {suggestions.map((text, idx) => (
                        <button
                          key={idx}
                          onMouseEnter={() => setHoveredIndex(idx)}
                          onClick={() => handleSendMessage(text)}
                          className={`w-full flex items-center py-3.5 rounded-2xl text-[14px] font-medium transition-all text-left outline-none focus:outline-none ${selectedIndex === idx ? "bg-white/10 text-white" : hoveredIndex === idx ? "bg-white/5 text-white/80" : "text-slate-400"}`}
                        >
                          <span className="w-8 shrink-0 flex items-center justify-center">
                            <ArrowRight
                              size={17}
                              className={`transition-all duration-200 ${selectedIndex === idx || hoveredIndex === idx ? "text-white opacity-100 translate-x-1.5" : "text-white/40 opacity-60 translate-x-0"}`}
                            />
                          </span>
                          <span className="flex-1 leading-tight pl-[10px]">{text}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-2.5">
                    <div
                      className={`bg-[#242424]/60 rounded-[24px] border transition-all duration-300 p-4 flex flex-col gap-3 ${isFocused ? "border-blue-500/30 bg-[#242424]/80 shadow-inner" : "border-white/5 shadow-sm"}`}
                      onClick={(e) => {
                        if (e.target.closest('button') || e.target.closest('input')) return;
                        setSelectedIndex(-1);
                        setIsFocused((prev) => !prev);
                      }}
                    >
                      {selectedChip && (
                        <div className="flex">
                          <div className="flex items-center gap-3 bg-blue-500/10 border border-blue-500/30 rounded-full pl-4 pr-3 py-1.5 animate-in fade-in zoom-in duration-300">
                            <QuoteArrow size={16} className="text-blue-400" />
                            <span className="text-xs font-semibold text-blue-200 italic">
                              {selectedChip}
                            </span>
                            <button
                              onClick={() => setSelectedChip(null)}
                              aria-label="Clear selected topic"
                              className="p-1 hover:bg-white/10 rounded-full transition-colors"
                            >
                              <X size={14} className="text-blue-400/60" />
                            </button>
                          </div>
                        </div>
                      )}
                      <input
                        ref={inputRef}
                        value={userInput}
                        onChange={(e) => {
                          setUserInput(e.target.value);
                          if (selectedIndex !== -1) setSelectedIndex(-1);
                        }}
                        onMouseDown={() => {
                          clickFocusingRef.current = !isFocused;
                        }}
                        onFocus={() => {
                          if (!clickFocusingRef.current) setIsFocused(true);
                          clickFocusingRef.current = false;
                        }}
                        onClick={() => {
                          setSelectedIndex(-1);
                          setIsFocused((prev) => !prev);
                        }}
                        onKeyDown={handleKeyDown}
                        maxLength={2000}
                        placeholder="Engage Neuralward Intelligence"
                        className="w-full bg-transparent border-none outline-none text-slate-200 placeholder:text-slate-600 px-2 text-lg font-medium ring-0 focus:ring-0"
                      />
                      <div className="flex items-center justify-between pt-2 border-t border-white/5">
                        <div className="flex items-center gap-1">
                          <button aria-label="Attach file" className="p-2 text-slate-500 hover:text-white transition-colors">
                            <Paperclip size={20} />
                          </button>
                          <button className="flex items-center gap-2.5 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] font-bold text-slate-300 uppercase tracking-wider group hover:bg-white/10 transition-all">
                            <NeuralwardLogo /> Sentry
                          </button>
                          <button
                            onClick={() => {
                              if (messages.length === 0) return;
                              const next = !sentryInvoked;
                              setSentryInvoked(next);
                              if (next) window.dispatchEvent(new CustomEvent("nw:sentryTrigger", { detail: "RESTING" }));
                            }}
                            disabled={messages.length === 0}
                            title={sentryInvoked ? "Back to chat" : "Show Sentry visual"}
                            className={`p-2 transition-colors disabled:opacity-40 ${
                              sentryInvoked
                                ? "text-[#ea3d95] hover:text-white"
                                : "text-slate-500 hover:text-white"
                            }`}
                          >
                            <Globe size={20} />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <button aria-label="Voice input" className="p-2 text-slate-500 hover:text-white transition-colors">
                            <Mic size={20} />
                          </button>
                          <button
                            onClick={() => handleSendMessage()}
                            disabled={isProcessing}
                            aria-label="Send message"
                            className="h-12 w-12 flex items-center justify-center rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-400 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                          >
                            <Send size={22} className="-ml-0.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>
  );

  if (standalone) {
    return (
      <div className="fixed inset-0 font-sans antialiased text-slate-200" ref={containerRef}>
        {expandedContent}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[999] font-sans antialiased text-slate-200"
      style={{ visibility: shouldHide ? "hidden" : "visible" }}
    >
      {isMobile ? (
        <>
          {/* Mobile tab — visible when drawer is closed */}
          <div
            className="fixed bottom-0 left-0 right-0 flex justify-center pointer-events-auto"
            style={{
              transform: drawerOpen ? "translateY(100%)" : "translateY(0)",
              transition: "transform 420ms cubic-bezier(0.34,1.56,0.64,1)",
              WebkitTapHighlightColor: "transparent",
            }}
            onClick={() => setDrawerOpen(true)}
          >
            <div
              className="flex flex-col items-center gap-2 px-8 pt-2 pb-24 -mb-20 cursor-pointer"
              style={{
                backgroundColor: "rgba(28,28,28,0.98)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "20px 20px 0 0",
              }}
            >
              <div className="w-10 h-1 rounded-full bg-white/30" />
              <div className="flex items-center gap-2 text-slate-300">
                {(messages.length > 0 || isProcessing) ? (
                  <MiniSentry active />
                ) : (
                  <MessageSquare size={16} />
                )}
                <span className="text-xs font-semibold tracking-wide">Sentry AI</span>
                {messages.length > 0 && (
                  <span className="h-4 w-4 rounded-full bg-pink-500 text-[9px] flex items-center justify-center font-bold">
                    {messages.filter(m => m.role === "ai").length}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Backdrop — blurs page and blocks interaction when drawer is open */}
          <div
            className="fixed inset-0"
            style={{
              backgroundColor: "rgba(0,0,0,0.55)",
              backdropFilter: drawerOpen ? "blur(6px)" : "none",
              WebkitBackdropFilter: drawerOpen ? "blur(6px)" : "none",
              opacity: drawerOpen ? 1 : 0,
              pointerEvents: drawerOpen ? "auto" : "none",
              transition: "opacity 400ms ease",
            }}
            onClick={() => setDrawerOpen(false)}
          />

          {/* Floor strip — same color as drawer, covers the spring-bounce gap at the screen bottom */}
          <div
            className="fixed bottom-0 left-0 right-0 pointer-events-none"
            style={{
              height: "80px",
              backgroundColor: "rgba(28,28,28,0.98)",
              opacity: drawerOpen ? 1 : 0,
              transition: "opacity 300ms ease",
            }}
          />

          {/* Mobile drawer panel */}
          <div
            ref={containerRef}
            className="fixed bottom-0 left-0 right-0 flex flex-col pointer-events-auto shadow-2xl backdrop-blur-3xl overflow-hidden"
            style={{
              height: "85vh",
              backgroundColor: "rgba(28,28,28,0.98)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "24px 24px 0 0",
              transform: drawerOpen ? "translateY(0)" : "translateY(100%)",
              transition: "transform 500ms cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            <div
              className="flex justify-center pt-3 pb-1 shrink-0 cursor-pointer"
              onClick={() => setDrawerOpen(false)}
              onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
              onTouchEnd={(e) => {
                if (touchStartY.current !== null) {
                  const delta = e.changedTouches[0].clientY - touchStartY.current;
                  if (delta > 60) setDrawerOpen(false);
                  touchStartY.current = null;
                }
              }}
            >
              <div className="w-12 h-1.5 rounded-full bg-white/25" />
            </div>
            {expandedContent}
          </div>
        </>
      ) : (
        /* Desktop: existing bottom-right panel */
        <div className={`h-full flex transition-all duration-500 ${isMaximized ? "items-center justify-center p-3" : "items-end justify-end p-4 md:p-8"}`}>
          <div
            className={`flex items-end justify-end relative transition-all duration-500 ${isMaximized ? "w-full h-full max-w-none max-h-none" : "w-full max-w-4xl h-[85vh] max-h-[900px]"}`}
            style={{
              transform: isVisible ? "translateY(0)" : "translateY(120px)",
              opacity: isVisible ? 1 : 0,
              transition:
                "transform 600ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 400ms ease",
            }}
          >
            <div
              ref={containerRef}
              onClick={() => {
                if (isMinimized) {
                  setIsMinimized(false);
                  setTimeout(() => {
                    inputRef.current?.focus();
                    setIsFocused(true);
                  }, 720);
                }
              }}
              className={`relative shadow-2xl flex flex-col overflow-hidden group/main backdrop-blur-3xl ${
                isMinimized
                  ? "w-16 h-16 rounded-2xl cursor-pointer hover:scale-105 active:scale-95"
                  : "w-full h-full rounded-[40px]"
              }`}
              style={{
                backgroundColor: "rgba(28, 28, 28, 0.98)",
                border: isMinimized
                  ? "1px solid rgba(255,255,255,0)"
                  : "1px solid rgba(255,255,255,0.1)",
                transition: isMinimized
                  ? "width 550ms cubic-bezier(0.34,1.56,0.64,1), height 550ms cubic-bezier(0.34,1.56,0.64,1), border-radius 550ms cubic-bezier(0.34,1.56,0.64,1), transform 150ms ease-in-out"
                  : "width 700ms cubic-bezier(0.34,1.56,0.64,1), height 700ms cubic-bezier(0.34,1.56,0.64,1), border-radius 700ms cubic-bezier(0.34,1.56,0.64,1)",
              }}
            >
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  opacity: isMinimized ? 1 : 0,
                  transform: isMinimized ? "scale(1)" : "scale(0.5)",
                  transition: isMinimized
                    ? "opacity 300ms, transform 300ms"
                    : "opacity 0ms, transform 0ms",
                  pointerEvents: isMinimized ? "auto" : "none",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent rounded-2xl" />
                <div className="relative z-10 isolate">
                  <MessageSquare
                    size={32}
                    className="text-blue-400 drop-shadow-[0_0_12px_rgba(59,130,246,0.4)]"
                  />
                  <div
                    ref={shineRef}
                    className="absolute inset-0 overflow-hidden icon-shine"
                  />
                </div>
                <div
                  ref={pulseRef}
                  className="absolute top-5 right-0 h-8 w-8 bg-pink-500 rounded-full animate-pulse-dot shadow-[0_0_10px_rgba(236,72,153,0.5)]"
                />
              </div>
              {expandedContent}
            </div>
          </div>
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes pulse-dot {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 1; animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); }
          13.79%, 100% { transform: translate(-50%, -50%) scale(6); opacity: 0; }
        }
        .animate-pulse-dot { animation: pulse-dot 5.8s linear infinite; }

        @keyframes icon-shine {
          0%, 22.41% { transform: skewX(-15deg) translateX(-200%); }
          29.31%, 100% { transform: skewX(-15deg) translateX(400%); }
        }
        .icon-shine {
          -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'/%3E%3C%2Fsvg%3E");
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'/%3E%3C%2Fsvg%3E");
          -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat; -webkit-mask-size: 100% 100%; mask-size: 100% 100%;
        }
        .icon-shine::after {
          content: ''; position: absolute; top: -50%; left: 0; width: 50%; height: 200%;
          background: linear-gradient(to right, transparent, rgba(255, 255, 255, 1) 50%, transparent);
          animation: icon-shine 5.8s linear infinite; pointer-events: none;
        }

        @keyframes custom-dot-pulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%, 0) scale(1); }
          50% { opacity: 1; transform: translate(-50%, 0) scale(1.15); box-shadow: 0 0 12px 2px rgba(59, 130, 246, 0.4); }
        }
        .dot-pulse { animation: custom-dot-pulse 2s infinite; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 10px; }
        *:focus { outline: none !important; }
      `,
        }}
      />
    </div>
  );
};

export default React.memo(NeuralAssistant);
